import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { networkSets } from 'src/constants/networkSets'
import { networkLogos } from 'src/constants/networkLogos'
import { defaultCTAURI } from 'src/modules/utils/constants'
import { selectCTAParams } from 'src/modules/reducers/params'
import { useSelector } from 'react-redux'
import { createUrl } from 'src/modules/utils/createUrl'
import { Link as ScrollLink } from 'react-scroll'
import { Link } from 'gatsby'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import CTAButton from 'src/components/common/CTAButtonNew'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import Image from '../Image'
import { useCtaText } from 'src/modules/hooks/useCtaText'
import {
  colors,
  spacing,
  borderRadius,
  mediaQuery,
  maxWidth,
  typographyMixin,
  heading,
} from 'src/constants/marketing-ds'
import { SectionContainer, SectionHeader } from 'src/components/common/SharedComponents'

// import gsap from 'gsap'
// import { useGSAP } from '@gsap/react'
// import { ScrollTrigger } from 'gsap/ScrollTrigger'

const handleNetworks = (networkSet, customNetworks) => {
  let selectedNetworks = []
  if (networkSet) {
    // find the passed network set and make a copy
    selectedNetworks = [...networkSets[networkSet]]
  } else {
    // or else, use the default set
    selectedNetworks = [...networkSets.default]
  }

  if (customNetworks?.length) {
    const addedNetworks = customNetworks
    const additionalNetworks = []
    addedNetworks.forEach((addition) => {
      const networkComponent = networkLogos[addition]
      additionalNetworks.push(networkComponent)
      // check if the custom network already exists in the chosen network set, if so, remove it
      const index = selectedNetworks.findIndex((network) => network === networkComponent)
      if (index > -1) {
        selectedNetworks.splice(index, 1)
      }
    })
    // finally, add the custom networks to the start of the set
    selectedNetworks.unshift(...additionalNetworks)
  }
  return selectedNetworks
}

const NetworkSection = ({
  ctaHrefOverride,
  ctaText,
  customNetworks,
  networkSet,
  headline,
  subtitle,
  maxCount,
  lang = 'en',
  market = 'us',
  noBorder,
  hasOfferInput,
  disableFreeTrial,
}) => {
  // get ctaParams from redux
  const parsedParams = useSelector(selectCTAParams)
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  let ctaURL = createUrl(defaultCTAURI, parsedParams)

  const networks = React.useMemo(() => {
    let networkSetKey = networkSet

    if (!networkSetKey) {
      const marketToNetworkSet = {
        us: 'default',
        canada: 'canadaEssentials',
        latino: 'entertainmentLatino',
      }
      networkSetKey = marketToNetworkSet[market] || 'default'
    }

    return handleNetworks(networkSetKey, customNetworks)
  }, [networkSet, customNetworks, market])

  const defaultCtaText = useCtaText({ lang, market, disableFreeTrial, ctaText })
  ctaURL = replaceUrlParam(ctaURL, 'comp', 'network')
  const ctaLink = hasOfferInput ? '#' : ctaHrefOverride || ctaURL
  const copyUS = {
    en: {
      headline: 'Stream live TV from 200+ channels you love',
      subtitle: 'Start for free. Cancel online anytime.',
    },
    es: {
      headline: 'Más de 200 canales en vivo de la TV que amas',
      subtitle: 'Prueba gratis. Cancela cuando quieras.',
    },
  }

  const copyCA = {
    headline: 'Stream the sports and entertainment you love',
    subtitle: 'Watch the Premier League, Serie A, Coppa Italia & so much more.',
  }

  const copyLatino = {
    en: {
      headline: 'The best of live TV and sports in Spanish',
      subtitle: 'Cancel online anytime.',
    },
    es: {
      headline: 'Lo mejor de la TV y el deporte en español',
      subtitle: 'Cancela cuando quieras',
    },
  }

  let copy = copyUS[lang]

  switch (market) {
    case 'canada':
      copy = copyCA
      break
    case 'latino':
      copy = copyLatino[lang]
      break
    default:
      copy = copyUS[lang]
  }

  const handleOnClick = (e) => {
    const isAnchor = ctaLink.startsWith('#')
    fireUIInteractionEvent(
      {
        element: copy.ctaText,
        component: 'network_section_cta_clicked',
        type: isAnchor ? 'clicked_anchor' : 'clicked',
      },
      stateForProtobuf
    )
  }

  const handleMoreClick = (e) => {
    fireUIInteractionEvent(
      {
        element: 'More',
        component: 'network_section_more_cta_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  const onLinkClick = (label) => {
    fireUIInteractionEvent(
      {
        element: label,
        component: 'network_item',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  return (
    <SectionContainer backgroundColor={colors.slate[190]}>
      <StyledNetworkSection noBorder={noBorder}>
        <div className="networks-wrapper">
          <SectionHeader title={headline || copy.headline} subtitle={subtitle || copy.subtitle} />

          {networks.length > 0 ? (
            <div className="networks-content">
              <div className="networks-row">
                {networks.length > 0
                  ? networks.slice(0, maxCount || 7).map((network, i) => {
                      const imgSrc = network.logoUrl
                      const url = network?.url
                      return url ? (
                        <Link key={i} to={url} onClick={() => onLinkClick(network.alt)}>
                          <li className="networks-item">
                            <Image
                              src={imgSrc}
                              alt={network.alt}
                              isLazyLoad={true}
                              isCompress={false}
                            />
                          </li>
                        </Link>
                      ) : (
                        <li key={i} className="networks-item">
                          <Image
                            src={imgSrc}
                            alt={network.alt}
                            isLazyLoad={true}
                            isCompress={false}
                          />
                        </li>
                      )
                    })
                  : null}
                <li className="networks-item">
                  <ScrollLink
                    to="channels-section"
                    smooth={true}
                    duration={500}
                    onClick={handleMoreClick}
                    className="more-text"
                  >
                    {lang === 'es' ? 'Y MÁS' : '+ MORE'}
                  </ScrollLink>
                </li>
              </div>
            </div>
          ) : null}

          <CTAButton link={ctaLink} onClick={handleOnClick}>
            {defaultCtaText}
          </CTAButton>
        </div>
      </StyledNetworkSection>
    </SectionContainer>
  )
}

export default NetworkSection

NetworkSection.propTypes = {
  lang: PropTypes.oneOf(['en', 'es']),
  plan: PropTypes.oneOf(['us', 'canada', 'latino']),
}

const StyledNetworkSection = styled.section`
  overflow: hidden;
  padding: ${spacing.xl} 0 ${spacing.lg};
  ${mediaQuery.tablet} {
    padding: ${spacing.xxxl} 0 ${spacing.lg};
  }
  ${mediaQuery.desktop} {
    padding: ${spacing.xxxl} 0 ${spacing.lg};
  }

  .networks-wrapper {
    max-width: ${maxWidth.desktop};
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${spacing.lg};
    ${mediaQuery.tablet} {
      gap: ${spacing.xl};
    }
    ${mediaQuery.desktop} {
      gap: ${spacing.xxl};
    }
  }

  .section-title {
    ${mediaQuery.tablet} {
      max-width: ${maxWidth.container};
    }
  }

  .networks-content {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .networks-row {
    width: auto;
    max-width: 972px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(4, 1fr);
    justify-its: center;
    gap: ${spacing.xs};

    ${mediaQuery.tablet} {
      gap: ${spacing.sm};
    }

    ${mediaQuery.desktop} {
      grid-template-columns: repeat(4, 1fr);
      grid-template-rows: repeat(2, 1fr);
      gap: ${spacing.md};
    }
  }

  .networks-item {
    width: 100%;
    max-width: 225px;
    background: ${colors.white};
    border: 2px solid ${colors.slate[160]};
    border-radius: ${borderRadius.sm};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    list-style: none;
    margin: 0;
    aspect-ratio: 166/99;
    ${mediaQuery.tablet} {
      aspect-ratio: 224/134;
    }

    img {
      object-fit: contain;
      width: 65%;
      height: 65%;
      box-sizing: border-box;
    }

    .more-text {
      text-align: center;
      ${typographyMixin(heading.md)}
      text-transform: uppercase;
      color: ${colors.semantic.text.inverse};
      white-space: nowrap;
      cursor: pointer;
      text-decoration: none;
    }
  }
`
